import { createContext, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import EmailLink from 'src/components/atoms/emailLink'
import { InvalidAccessTokenError } from 'src/hooks/useAPIRequest/constants'
import { useData } from 'src/state/dataState'

import { notification } from 'antd'
import axios from 'axios'

const { useNotification } = notification

const GLOBAL_ERROR_KEY = 'global_error_key'

const GlobalErrorContext = createContext(null)

interface GlobalError {
    url: string
}

export const GlobalErrorProvider = ({ children }: { children: React.ReactNode }) => {
    const { t } = useTranslation()

    const { contactInfo } = useData()

    const [notification, notificationContext] = useNotification()

    const [globalError, setGlobalError] = useState<GlobalError>(null)
    const [noNetworkError, setNoNetworkError] = useState(false)

    const showNotification = (description: string | React.ReactNode) => {
        return notification.error({
            key: GLOBAL_ERROR_KEY,
            duration: 0, // Disable auto-close
            message: t('An unexpected error occurred'),
            description,
        })
    }

    useEffect(() => {
        if (globalError?.url) {
            showNotification(t('Something went wrong, please try again.'))
        } else {
            notification.destroy(GLOBAL_ERROR_KEY)
        }
    }, [globalError?.url])

    useEffect(() => {
        if (noNetworkError) {
            showNotification(
                t('It looks like your are disconnected from the internet, please resolve this to prevent data loss.'),
            )
        } else {
            notification.destroy(GLOBAL_ERROR_KEY)
        }
    }, [noNetworkError])

    useEffect(() => {
        const globalErrorRequestInterceptor = axios.interceptors.request.use(
            (config) => {
                setNoNetworkError(false)
                return config
            },
            (error) => Promise.reject(error),
        )

        const globalErrorResponseInterceptor = axios.interceptors.response.use(
            (response) => {
                if (noNetworkError) {
                    setNoNetworkError(false)
                }

                if (response.config.url === globalError?.url) {
                    setGlobalError(null)
                }

                return response
            },
            (error) => {
                // Invalid access token should be passed down so we can log out the user.
                if (error instanceof InvalidAccessTokenError) {
                    return Promise.reject(error)
                }

                if (axios.isCancel(error)) {
                    return Promise.reject(error)
                }

                const networkError = !error.response
                if (networkError) {
                    setNoNetworkError(true)
                    return
                }

                // When an unexpected server error occurred, we use the general handler.
                // Exception: if the backend returned a known error key, pass it through to
                // the local handler so it can show a more specific message.
                const unexpectedError = error.response.status >= 500
                const hasKnownErrorKey = error.response?.data?.key !== undefined
                if (unexpectedError && !hasKnownErrorKey) {
                    if (globalError?.url) {
                        showNotification(
                            <Trans>
                                Looks like the error keeps occurring, please{' '}
                                <EmailLink key={0} email={contactInfo.support} message={t('contact support')} /> if the
                                issue cannot be resolved.
                            </Trans>,
                        )
                    } else {
                        setGlobalError({ url: error.config.url })
                    }

                    return
                }

                // Otherwise we send it through to be handled by a underlying handler
                return Promise.reject(error)
            },
        )

        return () => {
            axios.interceptors.request.eject(globalErrorRequestInterceptor)
            axios.interceptors.response.eject(globalErrorResponseInterceptor)
        }
    }, [globalError, noNetworkError])

    return (
        <GlobalErrorContext.Provider value={null}>
            {notificationContext}
            {children}
        </GlobalErrorContext.Provider>
    )
}
